import type { AccountProfile, AccountSnapshot, AgentTask, PlatformId, PostMetric, Topic } from "../types";
import { formatWhen } from "../lib/format";

export const DEFAULT_ACCOUNT_ID = "xiaohongshu";

export const XHS_NAME = "AI准点下班研究所";

export const PLACEHOLDER_DOUYIN_NAME = "抖音 · 个人 AI 酷步视频";

export const ADDABLE_PLATFORMS = ["小红书", "抖音", "X", "Instagram", "Facebook", "TikTok"] as const;

export type AddablePlatform = (typeof ADDABLE_PLATFORMS)[number];

const METRICS_PLATFORM: Record<string, PlatformId> = {
  小红书: "xiaohongshu",
  抖音: "douyin",
  X: "x",
  Instagram: "instagram",
};

export function isAccountId(value: string | null | undefined): value is string {
  return Boolean(value && /^[A-Za-z0-9_-]{1,80}$/.test(value));
}

/** Topics saved before workspaces existed belong to 小红书. */
export function topicAccount(topic: { accountId?: string | null }): string {
  const id = topic.accountId?.trim();
  if (!id || !isAccountId(id)) return DEFAULT_ACCOUNT_ID;
  return id;
}

export function isAddablePlatform(value: string): value is AddablePlatform {
  return (ADDABLE_PLATFORMS as readonly string[]).includes(value);
}

export function metricsPlatformId(platform: string): PlatformId | null {
  return METRICS_PLATFORM[platform] ?? null;
}

export function isSpokenPlatform(platform: string): boolean {
  return platform === "抖音" || platform === "TikTok";
}

export function parseAccountName(value: string): "xiaohongshu" | "douyin" | null {
  const text = value.trim();
  if (text === "抖音" || text.toLowerCase() === "douyin") return "douyin";
  if (text === "小红书" || text.toLowerCase() === "xiaohongshu") return "xiaohongshu";
  return null;
}

export interface WorkspaceData {
  topics: readonly Pick<Topic, "accountId" | "inTrash">[];
  tasks: readonly Pick<AgentTask, "accountId">[];
  snapshots: readonly Pick<AccountSnapshot, "platform" | "deletedAt">[];
  postMetrics: readonly Pick<PostMetric, "platform" | "deletedAt">[];
}

export function builtinAccount(profiles: readonly AccountProfile[]): AccountProfile {
  const stored = profiles.find((item) => item.id === DEFAULT_ACCOUNT_ID);
  return {
    id: DEFAULT_ACCOUNT_ID,
    platform: "小红书",
    displayName: stored?.displayName.trim() || XHS_NAME,
    profileUrl: stored?.profileUrl ?? "",
    note: stored?.note ?? "",
    addedAt: "builtin",
    updatedAt: stored?.updatedAt || "1970-01-01T00:00:00.000Z",
  };
}

/** Accounts the switcher may show. The 小红书 default stays. A douyin placeholder with no data stays hidden. */
export function listedAccounts(profiles: readonly AccountProfile[], data: WorkspaceData): AccountProfile[] {
  const rest = profiles.filter((item) => item.id !== DEFAULT_ACCOUNT_ID && accountIsListed(item, data));
  return [builtinAccount(profiles), ...rest];
}

export function accountIsListed(profile: AccountProfile, data: WorkspaceData): boolean {
  if (profile.id === DEFAULT_ACCOUNT_ID) return true;
  if (profile.addedAt) return true;
  return workspaceHasData(data, profile.id, profile.platform || platformFromId(profile.id));
}

export function workspaceHasData(data: WorkspaceData, accountId: string, platform: string): boolean {
  if (data.topics.some((topic) => !topic.inTrash && topicAccount(topic) === accountId)) return true;
  if (data.tasks.some((task) => task.accountId === accountId)) return true;
  const metricsId = metricsPlatformId(platform);
  if (!metricsId) return false;
  if (data.snapshots.some((row) => row.deletedAt == null && row.platform === metricsId)) return true;
  return data.postMetrics.some((row) => row.deletedAt == null && row.platform === metricsId);
}

export function platformOf(profiles: readonly AccountProfile[], accountId: string): string {
  if (!accountId || accountId === DEFAULT_ACCOUNT_ID) return "小红书";
  const found = profiles.find((item) => item.id === accountId);
  if (found?.platform) return found.platform;
  return platformFromId(accountId);
}

export function accountTitle(profile: Pick<AccountProfile, "displayName" | "platform">): string {
  return `${profile.displayName} · ${profile.platform}`;
}

export function accountInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.slice(0, 1).toUpperCase() : "账";
}

export function lastMetricSync(platform: string, snapshots: readonly AccountSnapshot[]): string | null {
  const metricsId = metricsPlatformId(platform);
  if (!metricsId) return null;
  const rows = snapshots.filter((row) => row.deletedAt == null && row.platform === metricsId);
  rows.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt) || right.recordedDate.localeCompare(left.recordedDate));
  return rows[0]?.updatedAt ?? null;
}

export function newAccountId(platform: string, profiles: readonly AccountProfile[]): string {
  if (platform === "抖音") {
    const placeholder = profiles.find((item) => item.id === "douyin" && !item.addedAt);
    if (placeholder) return placeholder.id;
    if (!profiles.some((item) => item.id === "douyin")) return "douyin";
  }
  return crypto.randomUUID();
}

export function accountConnected(
  platform: string,
  snapshots: readonly AccountSnapshot[],
  posts: readonly PostMetric[],
): boolean {
  if (lastMetricSync(platform, snapshots)) return true;
  const metricsId = metricsPlatformId(platform);
  if (!metricsId) return false;
  return posts.some((row) => row.deletedAt == null && row.platform === metricsId);
}

export function connectionLine(platform: string, snapshots: readonly AccountSnapshot[], posts: readonly PostMetric[]): string {
  const when = lastMetricSync(platform, snapshots);
  if (when || accountConnected(platform, snapshots, posts)) {
    return when ? `已连接 · 上次同步 ${formatWhen(when)}` : "已连接";
  }
  return "未连接（需要助理登录创作者中心）";
}

function platformFromId(accountId: string): string {
  if (accountId === "douyin") return "抖音";
  if (accountId === "x") return "X";
  if (accountId === "instagram") return "Instagram";
  return "小红书";
}
